import React, { useEffect, useRef, useState } from "react";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { buildClasses } from "../utils/buildClasses";
import { DropDownEntry, DropDownGroup } from "./RyeDropDown";
import { RyeIcon } from "./RyeIcon";
import {
  useFloating,
  offset,
  flip,
  shift,
  size as floatingSize,
  useInteractions,
  useDismiss,
  FloatingPortal,
  autoUpdate,
} from "@floating-ui/react";

// Import the icon type from RyeDropDown
type DropDownEntryIcon = null | string | JSX.Element;

type NavigableItem<TIdType> =
  | { type: "group"; group: DropDownGroup; entries: DropDownEntry<TIdType>[] }
  | { type: "entry"; entry: DropDownEntry<TIdType> }
  | { type: "create_new"; text: string };

function buildNavigableItems<TIdType>(
  entries: DropDownEntry<TIdType>[],
  groups?: DropDownGroup[],
): NavigableItem<TIdType>[] {
  if (!groups || groups.length === 0) {
    return entries.map((entry) => ({ type: "entry", entry }));
  }

  const groupMap = new Map<string, DropDownGroup>();
  groups.forEach((group) => groupMap.set(group.id, group));

  const groupedEntries = new Map<string, DropDownEntry<TIdType>[]>();
  const ungroupedEntries: DropDownEntry<TIdType>[] = [];

  entries.forEach((entry) => {
    if (entry.groupId && groupMap.has(entry.groupId)) {
      if (!groupedEntries.has(entry.groupId)) {
        groupedEntries.set(entry.groupId, []);
      }
      groupedEntries.get(entry.groupId)!.push(entry);
    } else {
      ungroupedEntries.push(entry);
    }
  });

  const items: NavigableItem<TIdType>[] = [];
  groups.forEach((group) => {
    const groupEntries = groupedEntries.get(group.id);
    if (groupEntries && groupEntries.length > 0) {
      items.push({ type: "group", group, entries: groupEntries });
      groupEntries.forEach((entry) => {
        items.push({ type: "entry", entry });
      });
    }
  });

  ungroupedEntries.forEach((entry) => {
    items.push({ type: "entry", entry });
  });

  return items;
}

export function RyeDropDownMultiSelect<TIdType>({
  selectedEntryIds = [],
  entries,
  groups,
  onSelectionChange,
  searchable = false,
  placeholder = "Search...",
  onEsc = () => {},
  onEntryClick = () => {},
  onClose = () => {},
  onCreateNew,
  showSelectedItemsRow = true,
  alwaysExpanded = false,
  initiallyExpanded = true,
  flipped: flippedProp,
  size = "md",
  height = "auto",
  width = "md",
  variant = "outlined",
  parentColor = "normal",
  groupHover = false,
  hookInputRef = () => {},
}: {
  selectedEntryIds?: TIdType[];
  entries: DropDownEntry<TIdType>[];
  groups?: DropDownGroup[];
  onSelectionChange: (selectedIds: TIdType[]) => void;
  searchable?: boolean;
  placeholder?: string;
  onEsc?: () => void;
  onEntryClick?: () => void;
  onClose?: () => void;
  // When provided, shows a "Create 'X'" entry in the dropdown when the filter
  // text doesn't exactly match an existing entry. Invoked with the filter text.
  onCreateNew?: (text: string) => void;
  showSelectedItemsRow?: boolean;
  // When true, the options list is always shown and cannot be collapsed by
  // internal dismiss. The parent is responsible for mounting/unmounting.
  alwaysExpanded?: boolean;
  // When false, the dropdown starts collapsed and is not auto-focused on mount.
  initiallyExpanded?: boolean;
  // When alwaysExpanded and the parent has flipped the dropdown above its
  // anchor, pass true so the menu is stacked above the input.
  flipped?: boolean;
  size?: "sm" | "md" | "lg";
  height?: "auto" | "full";
  width?: "sm" | "md" | "lg" | "full";
  variant?: "outlined" | "transparent";
  parentColor?: "normal" | "purple";
  groupHover?: boolean;
  hookInputRef?: (inputRef: React.RefObject<HTMLInputElement>) => void;
}): JSX.Element {
  const [isExpanded, setIsExpanded] = useState<boolean>(initiallyExpanded);
  const [filterValue, setFilterValue] = useState<string>("");
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  const { refs, floatingStyles, context, placement } = useFloating({
    open: isExpanded,
    onOpenChange: (open, _event, reason) => {
      if (!open) {
        close();
        if (reason === "escape-key") {
          onEsc();
        }
      }
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip(),
      shift(),
      floatingSize({
        apply({ rects }) {
          setMenuWidth(rects.reference.width);
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);

  function close() {
    if (alwaysExpanded) return;
    setIsExpanded(false);
    setFilterValue("");
    setHighlightIndex(null);
    onClose();
  }

  function toggleEntry(entry: DropDownEntry<TIdType>): void {
    const isSelected = selectedEntryIds.includes(entry.id);
    let newSelectedIds: TIdType[];

    if (isSelected) {
      newSelectedIds = selectedEntryIds.filter((id) => id !== entry.id);
    } else {
      newSelectedIds = [...selectedEntryIds, entry.id];
    }

    onSelectionChange(newSelectedIds);
    onEntryClick();
  }

  function toggleGroup(
    group: DropDownGroup,
    groupEntries: DropDownEntry<TIdType>[],
  ): void {
    const groupEntryIds = groupEntries.map((e) => e.id);
    const allSelected = groupEntryIds.every((id) =>
      selectedEntryIds.includes(id),
    );
    if (allSelected) {
      onSelectionChange(
        selectedEntryIds.filter((id) => !groupEntryIds.includes(id)),
      );
    } else {
      onSelectionChange(
        Array.from(new Set([...selectedEntryIds, ...groupEntryIds])),
      );
    }
    onEntryClick();
  }

  function getFilteredEntries(): DropDownEntry<TIdType>[] {
    if (!searchable || filterValue.trim() === "") {
      return entries;
    }
    return entries.filter((entry) =>
      entry.text.toLowerCase().includes(filterValue.toLowerCase()),
    );
  }

  const filteredEntries = getFilteredEntries();
  const navigableItems: NavigableItem<TIdType>[] = buildNavigableItems(
    filteredEntries,
    groups,
  );
  const trimmedFilter = filterValue.trim();
  const exactMatchExists = entries.some(
    (e) => e.text.toLowerCase() === trimmedFilter.toLowerCase(),
  );
  const showCreateNew =
    !!onCreateNew && trimmedFilter.length > 0 && !exactMatchExists;
  if (showCreateNew) {
    navigableItems.push({ type: "create_new", text: trimmedFilter });
  }

  function handleCreateNew(text: string) {
    onCreateNew?.(text);
    setFilterValue("");
    onEntryClick();
  }

  useEffect(() => {
    if (isExpanded) {
      setHighlightIndex(0);
    }
  }, [isExpanded, filterValue]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const totalItems = navigableItems.length;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!isExpanded) {
        setIsExpanded(true);
        setHighlightIndex(0);
      } else if (totalItems > 0) {
        setHighlightIndex((prev) =>
          prev === null || prev >= totalItems - 1 ? 0 : prev + 1,
        );
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (isExpanded && totalItems > 0) {
        setHighlightIndex((prev) =>
          prev === null || prev <= 0 ? totalItems - 1 : prev - 1,
        );
      }
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (isExpanded && highlightIndex !== null) {
        const item = navigableItems[highlightIndex];
        if (item) {
          if (item.type === "group") {
            toggleGroup(item.group, item.entries);
          } else if (item.type === "create_new") {
            handleCreateNew(item.text);
          } else {
            toggleEntry(item.entry);
          }
        }
      } else if (!isExpanded) {
        setIsExpanded(true);
        setHighlightIndex(0);
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (isExpanded) {
        close();
      }
      onEsc();
    }
  }

  const selectedEntries = entries.filter((e) =>
    selectedEntryIds.includes(e.id),
  );

  const isFlipped = alwaysExpanded
    ? (flippedProp ?? false)
    : placement.startsWith("top");

  const inputElement = (
    <SearchableDropDownInput
      isExpanded={isExpanded}
      isFlipped={isFlipped}
      setIsExpanded={setIsExpanded}
      displayText={placeholder}
      filterValue={filterValue}
      setFilterValue={setFilterValue}
      searchable={searchable}
      placeholder={placeholder}
      size={size}
      height={height}
      width={width}
      variant={variant}
      parentColor={parentColor}
      groupHover={groupHover}
      hookInputRef={hookInputRef}
    />
  );

  const menuElement = (
    <div
      className={buildClasses(
        "bg-surface-50",
        "border-tiny",
        "border-on-surface-200",
        { if: isFlipped, then: "rounded-t-md", else: "rounded-b-md" },
        "overflow-hidden",
        "shadow-popover",
      )}
    >
      <MultiSelectMenu
        navigableItems={navigableItems}
        selectedEntryIds={selectedEntryIds}
        onToggleEntry={toggleEntry}
        onToggleGroup={toggleGroup}
        onCreateNew={handleCreateNew}
        highlightIndex={highlightIndex}
        size={size}
      />
    </div>
  );

  return (
    <div
      ref={refs.setReference}
      className={buildClasses(
        "group/ryesearchabledropdown",
        "font-normal",
        {
          if: height === "full",
          then: "h-full",
        },
        {
          if: width === "sm",
          then: "w-32",
        },
        {
          if: width === "md",
          then: "w-48",
        },
        {
          if: width === "lg",
          then: "w-64",
        },
        {
          if: width === "full",
          then: "w-full",
        },
      )}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {showSelectedItemsRow && (
        <SelectedItemsRow
          selectedEntries={selectedEntries}
          onClearAll={() => onSelectionChange([])}
          size={size}
          variant={variant}
        />
      )}
      {alwaysExpanded ? (
        <>
          {isFlipped ? menuElement : inputElement}
          {isFlipped ? inputElement : menuElement}
        </>
      ) : (
        <>
          {inputElement}
          {isExpanded && (
            <FloatingPortal>
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, width: menuWidth }}
                {...getFloatingProps()}
                className={buildClasses("z-[2000000000]")}
              >
                {menuElement}
              </div>
            </FloatingPortal>
          )}
        </>
      )}
    </div>
  );
}

function SelectedItemsRow<TIdType>({
  selectedEntries,
  onClearAll,
  size,
  variant,
}: {
  selectedEntries: DropDownEntry<TIdType>[];
  onClearAll: () => void;
  size: "sm" | "md" | "lg";
  variant: "outlined" | "transparent";
}) {
  const isEmpty = selectedEntries.length === 0;
  return (
    <div
      className={buildClasses(
        "w-full",
        "flex",
        "items-center",
        "gap-2",
        "mb-1",
        "px-3",
        "bg-surface",
        {
          switch: size,
          cases: new Map([
            ["sm", buildClasses("h-7", "text-sm")],
            ["md", buildClasses("h-9", "text-md")],
            ["lg", buildClasses("h-11", "text-lg")],
          ]),
        },
        {
          if: variant === "outlined",
          then: buildClasses(
            "border-tiny",
            "border-on-surface-200",
            "rounded-md",
            "shadow-sm",
          ),
        },
      )}
    >
      <div
        className={buildClasses(
          "flex-1",
          "min-w-0",
          "flex",
          "items-center",
          "gap-1",
          "overflow-x-auto",
        )}
      >
        {isEmpty ? (
          <span
            className={buildClasses(
              "text-on-surface-400",
              "text-start",
              "truncate",
            )}
          >
            None selected
          </span>
        ) : (
          selectedEntries.map((entry) => (
            <span
              key={String(entry.id)}
              title={entry.text}
              className={buildClasses(
                "flex-shrink-0",
                "inline-flex",
                "items-center",
                "gap-1",
                "px-1.5",
                "py-0.5",
                "bg-purple-100",
                "text-purple-800",
                "rounded",
                "text-xs",
              )}
            >
              {entry.icon &&
                (typeof entry.icon === "string" ? (
                  <span className={buildClasses("text-base")}>
                    {entry.icon}
                  </span>
                ) : (
                  entry.icon
                ))}
              <span>{entry.text}</span>
            </span>
          ))
        )}
      </div>
      {!isEmpty && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClearAll();
          }}
          aria-label="Clear all"
          className={buildClasses(
            "flex-shrink-0",
            "inline-flex",
            "items-center",
            "justify-center",
            "text-on-surface-500",
            "hover:text-on-surface-800",
          )}
        >
          <RyeIcon name="close" size="sm" />
        </button>
      )}
    </div>
  );
}

// Reuse the SearchableDropDownInput from RyeDropDown
function SearchableDropDownInput({
  isExpanded,
  isFlipped,
  setIsExpanded,
  displayText,
  filterValue,
  setFilterValue,
  searchable,
  placeholder,
  size,
  height,
  width,
  variant,
  parentColor,
  groupHover,
  hookInputRef,
}: {
  isExpanded: boolean;
  isFlipped: boolean;
  setIsExpanded: (expanded: boolean) => void;
  displayText: string;
  filterValue: string;
  setFilterValue: (value: string) => void;
  searchable: boolean;
  placeholder: string;
  size: "sm" | "md" | "lg";
  height: "auto" | "full";
  width: "sm" | "md" | "lg" | "full";
  variant: "outlined" | "transparent";
  parentColor: "normal" | "purple";
  groupHover: boolean;
  hookInputRef: (inputRef: React.RefObject<HTMLInputElement>) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  return (
    <div
      className={buildClasses(
        "relative",
        "cursor-pointer",
        {
          if: height === "full",
          then: "h-full",
        },
        {
          if: width === "sm",
          then: "w-32",
        },
        {
          if: width === "md",
          then: "w-48",
        },
        {
          if: width === "lg",
          then: "w-64",
        },
        {
          if: width === "full",
          then: "w-full",
        },
      )}
    >
      <input
        ref={inputRef}
        type="text"
        value={searchable && isExpanded ? filterValue : displayText}
        onChange={(e) => setFilterValue(e.target.value)}
        onFocus={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        readOnly={!searchable}
        className={buildClasses(
          "w-full",
          "px-3",
          "pr-8",
          "bg-surface",
          "outline-none",
          "cursor-pointer",
          "text-on-surface-900",
          "text-start",
          {
            switch: size,
            cases: new Map([
              ["sm", buildClasses("h-8", "text-sm")],
              ["md", buildClasses("h-9", "text-md")],
              ["lg", buildClasses("h-11", "text-lg")],
            ]),
          },
          {
            if: height === "full",
            then: "h-full",
          },
          {
            if: variant === "outlined",
            then: buildClasses(
              "border-tiny",
              "border-on-surface-200",
              "rounded-md",
              "shadow-lg",
            ),
          },
          {
            if: variant === "outlined" && isExpanded && !isFlipped,
            then: buildClasses("rounded-b-none", "border-b-0"),
          },
          {
            if: variant === "outlined" && isExpanded && isFlipped,
            then: buildClasses("rounded-t-none", "border-t-0"),
          },
          {
            if: variant === "outlined" && parentColor === "purple",
            then: buildClasses("border-purple-300", "focus:border-purple-500"),
          },
          {
            if: groupHover && !isExpanded,
            then: buildClasses(
              "group-hover/ryesearchabledropdown:border-tiny",
              "group-hover/ryesearchabledropdown:border-on-surface-200",
            ),
          },
        )}
        placeholder={searchable && isExpanded ? placeholder : displayText}
      />
    </div>
  );
}

function MultiSelectMenu<TIdType>({
  navigableItems,
  selectedEntryIds,
  onToggleEntry,
  onToggleGroup,
  onCreateNew,
  highlightIndex,
  size,
}: {
  navigableItems: NavigableItem<TIdType>[];
  selectedEntryIds: TIdType[];
  onToggleEntry: (entry: DropDownEntry<TIdType>) => void;
  onToggleGroup: (
    group: DropDownGroup,
    entries: DropDownEntry<TIdType>[],
  ) => void;
  onCreateNew: (text: string) => void;
  highlightIndex: number | null;
  size: "sm" | "md" | "lg";
}) {
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (highlightIndex !== null) {
      itemRefs.current[highlightIndex]?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightIndex]);

  return (
    <OverlayScrollbarsComponent
      options={{
        scrollbars: { autoHide: "scroll" },
      }}
      className={buildClasses("max-h-48")}
    >
      <div>
        {navigableItems.map((item, index) => {
          const isHighlighted = highlightIndex === index;
          if (item.type === "create_new") {
            return (
              <div
                key={`create_new_${item.text}`}
                ref={(el) => (itemRefs.current[index] = el)}
                className={buildClasses(
                  "px-3",
                  "cursor-pointer",
                  "flex",
                  "items-center",
                  "gap-2",
                  "text-on-surface-500",
                  {
                    switch: size,
                    cases: new Map([
                      ["sm", buildClasses("h-8", "text-sm")],
                      ["md", buildClasses("h-9", "text-md")],
                      ["lg", buildClasses("h-11", "text-lg")],
                    ]),
                  },
                  {
                    if: isHighlighted,
                    then: "bg-on-surface-100",
                    else: buildClasses(
                      "bg-surface-50",
                      "hover:bg-on-surface-50",
                    ),
                  },
                )}
                onClick={() => onCreateNew(item.text)}
              >
                <span
                  className={buildClasses(
                    "flex-shrink-0",
                    "flex",
                    "items-center",
                    "justify-center",
                    {
                      switch: size,
                      cases: new Map([
                        ["sm", "w-2"],
                        ["md", "w-2.5"],
                        ["lg", "w-3"],
                      ]),
                    },
                  )}
                >
                  <RyeIcon
                    name="add"
                    size={size === "sm" ? "sm" : "md"}
                    className={buildClasses("text-on-surface-500")}
                  />
                </span>
                <span className={buildClasses("truncate")}>
                  Create &ldquo;{item.text}&rdquo;
                </span>
              </div>
            );
          }
          if (item.type === "group") {
            const groupEntryIds = item.entries.map((e) => e.id);
            const allGroupItemsSelected = groupEntryIds.every((id) =>
              selectedEntryIds.includes(id),
            );
            const howManyGroupItemsSelected = groupEntryIds.filter((id) =>
              selectedEntryIds.includes(id),
            ).length;

            return (
              <div
                key={`group_header_${item.group.id}`}
                ref={(el) => (itemRefs.current[index] = el)}
                className={buildClasses(
                  "px-3",
                  "py-2",
                  "text-xs",
                  "font-semibold",
                  "cursor-pointer",
                  {
                    if: isHighlighted,
                    then: "bg-on-surface-100",
                    else: "bg-surface-100",
                  },
                  {
                    if: allGroupItemsSelected,
                    then: "text-purple-900",
                    elseIfs: [
                      {
                        elseIf: howManyGroupItemsSelected > 0,
                        then: "text-purple-700",
                      },
                    ],
                    else: "text-on-surface-500",
                  },
                  "hover:bg-on-surface-100",
                )}
                onClick={() => onToggleGroup(item.group, item.entries)}
              >
                <div
                  className={buildClasses(
                    "flex",
                    "items-center",
                    "justify-between",
                  )}
                >
                  <span className={buildClasses("text-xs")}>
                    {item.group.label}
                  </span>
                  <div
                    className={buildClasses("flex", "items-center", "gap-2")}
                  >
                    <span
                      className={buildClasses(
                        "text-xs",
                        "font-normal",
                        "opacity-60",
                      )}
                    >
                      {allGroupItemsSelected ? "Deselect all" : "Select all"}
                    </span>
                    <RoundedCheckbox
                      state={
                        allGroupItemsSelected
                          ? "checked"
                          : howManyGroupItemsSelected > 0
                            ? "indeterminate"
                            : "unchecked"
                      }
                      size={size}
                    />
                  </div>
                </div>
              </div>
            );
          }

          const isSelected = selectedEntryIds.includes(item.entry.id);
          return (
            <MultiSelectMenuItem
              key={`entry_${item.entry.id}`}
              itemRef={(el) => (itemRefs.current[index] = el)}
              text={item.entry.text}
              icon={item.entry.icon}
              isSelected={isSelected}
              isHighlighted={isHighlighted}
              size={size}
              onClick={() => onToggleEntry(item.entry)}
            />
          );
        })}
      </div>
    </OverlayScrollbarsComponent>
  );
}

function MultiSelectMenuItem({
  itemRef,
  text,
  icon,
  isSelected,
  isHighlighted,
  size,
  onClick,
}: {
  itemRef?: (el: HTMLDivElement | null) => void;
  text: string;
  icon: DropDownEntryIcon;
  isSelected: boolean;
  isHighlighted: boolean;
  size: "sm" | "md" | "lg";
  onClick: () => void;
}) {
  return (
    <div
      ref={itemRef}
      className={buildClasses(
        "px-3",
        "cursor-pointer",
        "flex",
        "items-center",
        "justify-between",
        "gap-2",
        {
          switch: size,
          cases: new Map([
            ["sm", buildClasses("h-8", "text-sm")],
            ["md", buildClasses("h-9", "text-md")],
            ["lg", buildClasses("h-11", "text-lg")],
          ]),
        },
        {
          if: isHighlighted,
          then: "bg-on-surface-100",
          else: buildClasses("bg-surface-50", "hover:bg-on-surface-50"),
        },
      )}
      onClick={onClick}
    >
      <div className={buildClasses("flex", "items-center", "gap-2", "min-w-0")}>
        {icon && (
          <span className={buildClasses("flex-shrink-0")}>
            {typeof icon === "string" ? icon : icon}
          </span>
        )}
        <span className={buildClasses("truncate")}>{text}</span>
      </div>
      <RoundedCheckbox
        state={isSelected ? "checked" : "unchecked"}
        size={size}
      />
    </div>
  );
}

function RoundedCheckbox({
  state,
  size,
}: {
  state: "checked" | "unchecked" | "indeterminate";
  size: "sm" | "md" | "lg";
}) {
  const isOn = state !== "unchecked";
  return (
    <span
      className={buildClasses(
        "flex-shrink-0",
        "inline-flex",
        "items-center",
        "justify-center",
        "rounded-[0.3rem]",
        "border-2",
        "box-border",
        {
          switch: size,
          cases: new Map([
            ["sm", "w-4 h-4"],
            ["md", "w-[18px] h-[18px]"],
            ["lg", "w-5 h-5"],
          ]),
        },
        {
          if: isOn,
          then: buildClasses("bg-purple-600", "border-purple-600"),
          else: buildClasses("bg-transparent", "border-on-surface-400"),
        },
      )}
    >
      {state === "checked" && (
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={buildClasses("w-full", "h-full")}
        >
          <polyline points="3.5,8.5 6.5,11.5 12.5,5" />
        </svg>
      )}
      {state === "indeterminate" && (
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          className={buildClasses("w-full", "h-full")}
        >
          <line x1="4" y1="8" x2="12" y2="8" />
        </svg>
      )}
    </span>
  );
}
